/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Switch, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { styles } from '../posShared.styles';
import {
  resolveInvoiceType,
  spanishInvoiceTypeLabel,
  type PosFiscalBuyerDraft,
  type TaxRegime,
} from '../../../fiscal/domain-api';
import { getFiscalContext } from '../../../fiscal/infra-api';
import { PosFiscalBuyerSection } from '../../../fiscal/public-api';
import type { PosFiscalMode } from '../../domain/posTypes';
import { usePosCheckoutContextOptional } from '../contexts/PosCheckoutContext';

type Props = {
  allowFiscalSaleMode?: boolean;
  saleFiscalMode?: PosFiscalMode;
  onSetSaleFiscalMode?: (mode: PosFiscalMode) => void;
  fiscalBuyerDraft?: PosFiscalBuyerDraft;
  onFiscalBuyerDraftChange?: (patch: Partial<PosFiscalBuyerDraft>) => void;
  currentBranchId?: string | null;
  isSaving?: boolean;
  collapsible: boolean;
  fiscalExpanded: boolean;
  onToggleFiscal: () => void;
};

export function PosCheckoutFiscalPanel(props: Props) {
  const ctx = usePosCheckoutContextOptional();
  const {
    allowFiscalSaleMode = false,
    saleFiscalMode = 'non_fiscal',
    onSetSaleFiscalMode = () => {},
    fiscalBuyerDraft,
    onFiscalBuyerDraftChange,
    currentBranchId = null,
    isSaving = false,
    collapsible,
    fiscalExpanded,
    onToggleFiscal,
  } = {
    ...(ctx ?? {}),
    ...props,
  };
  const isFiscalSale = saleFiscalMode === 'fiscal';

  const [emitterRegime, setEmitterRegime] = useState<TaxRegime | null>(null);
  const [fiscalContextError, setFiscalContextError] = useState<string | null>(null);

  useEffect(() => {
    if (!isFiscalSale || !currentBranchId) {
      setEmitterRegime(null);
      setFiscalContextError(null);
      return;
    }
    let cancelled = false;
    // BUG-4 fix: getFiscalContext no lanza errores — retorna { profile: null } en fallo.
    // El .catch() nunca se ejecutaba, y profile null caía silenciosamente a 'monotributo'.
    // Ahora verificamos profile null en el .then() y mostramos error.
    getFiscalContext(currentBranchId)
      .then((fiscalCtx) => {
        if (cancelled) return;
        if (!fiscalCtx.profile) {
          setEmitterRegime(null);
          setFiscalContextError('No se pudo verificar el régimen fiscal. Revisá la configuración ARCA.');
          return;
        }
        setEmitterRegime(fiscalCtx.profile.taxRegime);
        setFiscalContextError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setEmitterRegime(null);
        setFiscalContextError('No se pudo verificar el régimen fiscal. Revisá la configuración ARCA.');
      });
    return () => { cancelled = true; };
  }, [isFiscalSale, currentBranchId]);

  const resolvedInvoiceLabel = (() => {
    if (!isFiscalSale || !emitterRegime) return null;
    const buyerTaxCondition = fiscalBuyerDraft?.consumidorFinal
      ? 5
      : fiscalBuyerDraft?.taxCondition ?? null;
    const cbteTipo = resolveInvoiceType({ emitterRegime, buyerTaxCondition });
    return spanishInvoiceTypeLabel(cbteTipo);
  })();

  return (
    <View style={[styles.checkoutSectionBlock, collapsible && styles.checkoutAccordion]}>
      {collapsible ? (
        <TouchableOpacity
          style={styles.checkoutAccHead}
          onPress={onToggleFiscal}
          accessibilityRole="button"
          accessibilityState={{ expanded: fiscalExpanded }}
        >
          <Text style={styles.checkoutAccHeadText}>Comprobante fiscal</Text>
          <Ionicons
            name={fiscalExpanded ? 'chevron-up' : 'chevron-down'}
            size={14}
            color={ticketColors.ink3}
          />
        </TouchableOpacity>
      ) : (
        <View style={styles.checkoutDetailTitleRow}>
          <Text style={styles.checkoutSecTitle}>Comprobante fiscal</Text>
        </View>
      )}
      {!collapsible || fiscalExpanded ? (
        <View style={styles.checkoutAccBody}>
          {allowFiscalSaleMode ? (
            <View
              style={[
                styles.checkoutFiscalSwitchRow,
                isFiscalSale && styles.checkoutFiscalSwitchRowActive,
              ]}
            >
              <View style={styles.checkoutFiscalSwitchLeading}>
                <View style={styles.checkoutFiscalSwitchText}>
                  <Text style={styles.checkoutFiscalSwitchTitle}>Tipo de venta</Text>
                  <Text style={styles.checkoutFiscalSwitchHint} numberOfLines={2}>
                    {isFiscalSale
                      ? 'Sale factura en ARCA.'
                      : 'Venta interna. No se envía a ARCA.'}
                  </Text>
                </View>
              </View>
              <Switch
                value={isFiscalSale}
                onValueChange={(enabled) => onSetSaleFiscalMode(enabled ? 'fiscal' : 'non_fiscal')}
                trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                thumbColor={isFiscalSale ? ticketColors.goldInk : ticketColors.paper2}
                accessibilityRole="switch"
                accessibilityLabel={`Tipo de venta: ${isFiscalSale ? 'fiscal' : 'no fiscal'}`}
                accessibilityState={{ checked: isFiscalSale }}
              />
            </View>
          ) : (
            <Text style={styles.checkoutNonFiscalBodyInline} numberOfLines={3}>
              Ticket interno. Consumidor final. No va a ARCA.
            </Text>
          )}

          {isFiscalSale && resolvedInvoiceLabel ? (
            <View style={styles.checkoutFiscalInvoiceTypeRow}>
              <Text style={styles.checkoutFiscalInvoiceTypeLabel}>Tipo de comprobante</Text>
              <Text style={styles.checkoutFiscalInvoiceTypeValue}>{resolvedInvoiceLabel}</Text>
            </View>
          ) : null}

          {isFiscalSale && fiscalContextError ? (
            <Text style={{ color: ticketColors.bad, fontSize: 12, marginTop: 4 }}>
              {fiscalContextError}
            </Text>
          ) : null}

          {isFiscalSale && fiscalBuyerDraft && onFiscalBuyerDraftChange ? (
            <PosFiscalBuyerSection
              branchId={currentBranchId}
              draft={fiscalBuyerDraft}
              onDraftChange={onFiscalBuyerDraftChange}
              disabled={isSaving}
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
